import Phaser from "phaser";
import {
  BOARD_IMG_WIDTH,
  BOARD_IMG_HEIGHT,
  BOARD_OFFSET_Y,
  UI_PANEL_HEIGHT,
  TEX_BACKGROUND,
  TEX_BOARD,
} from "../constants";
import { requestToken, fetchScores } from "../api";

const BOARD_OVERLAY_ALPHA = 1;
const TEX_TITLE = "title-logo";

export class StartScene extends Phaser.Scene {
  private started = false;
  private content!: Phaser.GameObjects.Container;
  private bg!: Phaser.GameObjects.Image;

  constructor() {
    super("StartScene");
  }

  preload(): void {
    if (!this.textures.exists(TEX_BACKGROUND)) {
      this.load.image(TEX_BACKGROUND, "assets/sprites/background.png");
    }
    if (!this.textures.exists(TEX_BOARD)) {
      this.load.image(TEX_BOARD, "assets/sprites/board.png?v=alpha1");
    }
    if (!this.textures.exists(TEX_TITLE)) {
      this.load.image(TEX_TITLE, "assets/sprites/title.png");
    }
  }

  create(): void {
    this.started = false;

    const contentW = BOARD_IMG_WIDTH;
    const contentH = BOARD_OFFSET_Y + BOARD_IMG_HEIGHT + UI_PANEL_HEIGHT;

    // All title content is built in content-local coords (0..contentW/H) inside a
    // container that layout() scales + centers so it fits any window size.
    this.content = this.add.container(0, 0);
    this.drawBoardBackground();

    const contentCenterX = contentW * 0.5;
    const contentCenterY = contentH * 0.5;

    this.content.add(this.add.rectangle(contentCenterX, contentCenterY, contentW, contentH, 0x000000, 0.34));

    const panelW = Math.min(contentW - 120, 900);
    const panelH = Math.min(contentH - 90, 900);
    const panelX = (contentW - panelW) * 0.5;
    const panelY = (contentH - panelH) * 0.5;

    this.content.add(this.add
      .rectangle(panelX, panelY, panelW, panelH, 0x101116, 0.78)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0xb48a56, 0.9));

    const buttonW = 320;
    const buttonH = 62;
    const footerPadding = 34;
    const hintHeight = 24;
    const hintGap = 12;
    const ctaTopGap = 20;
    const minBodyH = 320;

    const buttonY = panelY + panelH - footerPadding - buttonH * 0.5;
    const hintY = buttonY - buttonH * 0.5 - hintGap - hintHeight * 0.5;
    const bodyBottom = hintY - hintHeight * 0.5 - ctaTopGap;

    const titleY = panelY + 28;
    const titleSprite = this.add.image(contentCenterX, titleY, TEX_TITLE).setOrigin(0.5, 0);
    this.content.add(titleSprite);
    const titleFrame = this.textures.getFrame(TEX_TITLE);
    if (titleFrame && titleFrame.width > 0 && titleFrame.height > 0) {
      const maxTitleW = panelW - 120;
      const maxTitleHByLayout = Math.max(72, bodyBottom - titleY - minBodyH - 24);
      const desiredTitleScale = Math.min(maxTitleW / titleFrame.width, 170 / titleFrame.height) * 1.45;
      const maxLayoutScale = maxTitleHByLayout / titleFrame.height;
      const titleScale = Math.max(0.3, Math.min(desiredTitleScale, maxLayoutScale));
      titleSprite.setScale(titleScale);
    }
    const titleBottom = titleY + titleSprite.displayHeight;

    const bodyX = panelX + 36;
    const bodyY = titleBottom + 24;
    const bodyBoxW = panelW - 72;
    const bodyW = bodyBoxW;
    const bodyH = Math.max(80, bodyBottom - bodyY);

    this.content.add(this.add
      .rectangle(contentCenterX, bodyY + bodyH * 0.5, bodyBoxW + 20, bodyH + 18, 0x0b0f18, 0.5)
      .setStrokeStyle(1, 0xffffff, 0.1)
      .setDepth(1));

    // ── How to play (hidden by default) ──

    const helpBody = [
      "How it works",
      "- You control the light pieces. The enemy moves right after you.",
      "- Standard chess movement. Pawns auto-promote to queens.",
      "- No castling, no en passant, no check/checkmate —",
      "  the king is just a regular piece.",
      "- Newly spawned enemies have summoning sickness (faded)",
      "  and cannot capture on their first turn.",
      "",
      "The wrap mechanic (core of the game)",
      "- The board wraps when your back rank (bottom row) is empty.",
      "- The whole board shifts down and the bottom row drops off.",
      "- 1–2 new enemy pieces spawn at the top each wrap.",
      "- Enemy pieces on your back rank try to move off to let it wrap.",
      "- If you make 4 moves without a wrap, the enemy reinforces.",
      "",
      "Scoring",
      "- Piece values: pawn=1, knight=3, bishop=3, rook=5, queen=9.",
      "- + rows survived × 10, + time bonus (1 pt per 5 seconds).",
      "",
      "How to lose",
      "- You lose when you have no pieces left or no legal moves.",
      "- It's endless survival — there is no win condition.",
      "  Survive as long as you can!",
    ].join("\n");

    const makeHelpText = (fontSize: number): Phaser.GameObjects.Text => this.add
      .text(bodyX, bodyY, helpBody, {
        fontFamily: "system-ui, sans-serif",
        fontSize: `${fontSize}px`,
        color: "#eef2f7",
        lineSpacing: 4,
        wordWrap: { width: bodyW },
      })
      .setOrigin(0, 0);

    let helpFontSize = 24;
    let helpText = makeHelpText(helpFontSize);
    while (helpText.height > bodyH && helpFontSize > 16) {
      helpText.destroy();
      helpFontSize -= 1;
      helpText = makeHelpText(helpFontSize);
    }
    helpText.setDepth(2).setVisible(false);
    helpText.setCrop(0, 0, bodyW, bodyH);
    this.content.add(helpText);

    // ── Leaderboard (visible by default) ──

    const leaderboardContainer = this.add.container(0, 0).setDepth(2);
    this.content.add(leaderboardContainer);

    leaderboardContainer.add(
      this.add.text(contentCenterX, bodyY + 18, "── TOP 10 ──", {
        fontFamily: "monospace",
        fontSize: "22px",
        color: "#b48a56",
      }).setOrigin(0.5, 0),
    );

    const lbLoading = this.add.text(contentCenterX, bodyY + 52, "Loading...", {
      fontFamily: "monospace",
      fontSize: "16px",
      color: "#8a9aba",
    }).setOrigin(0.5, 0);
    leaderboardContainer.add(lbLoading);

    void fetchScores()
      .then((data) => {
        const lines = data.scores.length === 0
          ? ["(no scores yet)"]
          : data.scores.map((s, i) => {
              const rank = `${i + 1}.`.padEnd(3);
              const name = s.name.substring(0, 12).padEnd(12);
              return `  ${rank} ${name} ${s.score}`;
            });

        lbLoading.destroy();

        leaderboardContainer.add(
          this.add.text(contentCenterX, bodyY + 52, lines.join("\n"), {
            fontFamily: "monospace",
            fontSize: "18px",
            color: "#eef2f7",
            lineSpacing: 4,
          }).setOrigin(0.5, 0),
        );
      })
      .catch(() => {
        lbLoading.setText("(unavailable)");
      });

    this.content.add(this.add
      .rectangle(contentCenterX, buttonY - 6, panelW - 22, 124, 0x0f1015, 0.82)
      .setStrokeStyle(1, 0xb48a56, 0.4)
      .setDepth(3));

    const buttonBg = this.add
      .rectangle(contentCenterX, buttonY, buttonW, buttonH, 0x2d7f4d, 1)
      .setStrokeStyle(2, 0xe6f8dd, 0.95)
      .setInteractive({ useHandCursor: true })
      .setDepth(5);
    this.content.add(buttonBg);

    const buttonLabel = this.add
      .text(contentCenterX, buttonY, "Start Run", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "32px",
        color: "#f4fff6",
      })
      .setOrigin(0.5, 0.5)
      .setDepth(6);
    this.content.add(buttonLabel);

    const hintLabel = this.add
      .text(contentCenterX, hintY, "Enter / Space = Play  ·  H = How to Play", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "20px",
        color: "#d8dfeb",
      })
      .setOrigin(0.5, 0.5)
      .setDepth(6);
    this.content.add(hintLabel);

    // Toggle between leaderboard and how-to-play with H key
    let showingHelp = false;
    this.input.keyboard?.on("keydown-H", () => {
      showingHelp = !showingHelp;
      helpText.setVisible(showingHelp);
      leaderboardContainer.setVisible(!showingHelp);
      hintLabel.setText(
        showingHelp
          ? "Enter / Space = Play  ·  H = Leaderboard"
          : "Enter / Space = Play  ·  H = How to Play",
      );
    });

    const startGame = async () => {
      if (this.started) return;
      this.started = true;
      buttonBg.disableInteractive();
      buttonLabel.setText("Loading...");
      hintLabel.setText("Preparing board...");
      try {
        const [tokenRes] = await Promise.all([
          requestToken().catch(() => null),
          (async () => {
            if (this.scene.manager.keys.GameScene) return;
            const { GameScene } = await import("./GameScene");
            this.scene.add("GameScene", GameScene, false);
          })(),
        ]);
        this.scene.start("GameScene", { token: tokenRes?.token ?? null });
      } catch (err) {
        this.started = false;
        buttonBg.setInteractive({ useHandCursor: true });
        buttonLabel.setText("Start Run");
        hintLabel.setText("Load failed. Press Enter, Space, or click to retry.");
        console.error("Failed to start game", err);
      }
    };

    buttonBg.on("pointerover", () => {
      buttonBg.setFillStyle(0x35945a, 1);
      buttonLabel.setScale(1.03);
    });
    buttonBg.on("pointerout", () => {
      buttonBg.setFillStyle(0x2d7f4d, 1);
      buttonLabel.setScale(1);
    });
    buttonBg.on("pointerdown", startGame);

    this.input.keyboard?.on("keydown-ENTER", startGame);
    this.input.keyboard?.on("keydown-SPACE", startGame);

    // Fill the whole window on load and on every browser resize.
    const onResize = () => this.layout();
    this.scale.on(Phaser.Scale.Events.RESIZE, onResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.scale.off(Phaser.Scale.Events.RESIZE, onResize));
    this.layout();
  }

  /** Scale + center the title content to fit the window; cover the background. */
  private layout(): void {
    const vw = this.scale.width;
    const vh = this.scale.height;
    const contentW = BOARD_IMG_WIDTH;
    const contentH = BOARD_OFFSET_Y + BOARD_IMG_HEIGHT + UI_PANEL_HEIGHT;

    const scale = Math.min(vw / contentW, vh / contentH);
    const originX = Math.round((vw - contentW * scale) * 0.5);
    const originY = Math.round((vh - contentH * scale) * 0.5);
    this.content.setPosition(originX, originY).setScale(scale);

    if (this.bg) {
      this.bg.setPosition(vw * 0.5, vh * 0.5);
      const f = this.textures.getFrame(TEX_BACKGROUND);
      if (f && f.width > 0 && f.height > 0) this.bg.setScale(Math.max(vw / f.width, vh / f.height));
      else this.bg.setDisplaySize(vw, vh);
    }
  }

  private drawBoardBackground(): void {
    // Covers the viewport behind the content (sized in layout()).
    this.bg = this.add.image(0, 0, TEX_BACKGROUND).setOrigin(0.5, 0.5).setDepth(-1000);

    // Faint board artwork behind the title panel, in content-local coords.
    this.content.add(this.add
      .image(0, BOARD_OFFSET_Y, TEX_BOARD)
      .setOrigin(0, 0)
      .setAlpha(BOARD_OVERLAY_ALPHA));
  }
}